import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Container,
    Grid,
    Card,
    Chip,
    Avatar,
    Paper,
    Stack,
    Button
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
    TrendingUp as TrendingUpIcon,
    Inventory as InventoryIcon,
    Psychology as PsychologyIcon,
    AutoAwesome as AutoAwesomeIcon,
    CheckCircle as CheckCircleIcon,
    Speed as SpeedIcon,
    MonetizationOn as MoneyIcon,
    Warning as WarningIcon,
    TrendingDown as TrendingDownIcon,
    Insights as InsightsIcon,
    Update as UpdateIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

const DemandForecasting = () => {
    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Mock Live Stock Data for Hero Animation
    const [stockUpdates, setStockUpdates] = useState([
        { id: 1, product: 'Nike Air Zoom', status: 'critical', message: 'Stok kritik seviyede (%12)', action: 'Reorder' },
        { id: 2, product: 'Adidas Ultraboost', status: 'optimal', message: 'Satış tahmini: +15%', action: 'Stable' },
        { id: 3, product: 'Puma RS-X', status: 'overstock', message: 'Fazla stok uyarısı', action: 'Discount' },
    ]);

    useEffect(() => {
        const interval = setInterval(() => {
            setStockUpdates(prev => {
                const products = ['Nike Air Force', 'Vans Old Skool', 'Converse All Star', 'New Balance 530'];
                const statuses = ['critical', 'optimal', 'overstock'];
                const randomProduct = products[Math.floor(Math.random() * products.length)];
                const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];

                let message = '';
                let action = '';
                if (randomStatus === 'critical') { message = 'Stok bitmek üzere!'; action = 'Reorder'; }
                else if (randomStatus === 'optimal') { message = 'Talep artışı bekleniyor'; action = 'Stable'; }
                else { message = 'Stok devir hızı düşük'; action = 'Discount'; }

                const newUpdate = {
                    id: Date.now(),
                    product: randomProduct,
                    status: randomStatus,
                    message: message,
                    action: action
                };
                return [newUpdate, ...prev.slice(0, 2)];
            });
        }, 3500);
        return () => clearInterval(interval);
    }, []);

    const features = [
        {
            icon: <PsychologyIcon sx={{ fontSize: 40 }} />,
            title: 'Gelecek Talebi Öngörme',
            description: 'Satış geçmişi ve sezonsal verileri analiz ederek gelecekteki talebi yüksek doğrulukla öngörür.',
            color: '#2979ff'
        },
        {
            icon: <InventoryIcon sx={{ fontSize: 40 }} />,
            title: 'Stok Optimizasyonu',
            description: 'Ürün bazında stok seviyelerini tahminleyerek stok fazlasını veya eksiğini önler.',
            color: '#00e676'
        },
        {
            icon: <InsightsIcon sx={{ fontSize: 40 }} />,
            title: 'Gerçek Zamanlı Raporlama',
            description: 'Aktif stok, gerçekleşen satış ve tahmin verilerini tek ekranda gösteren KPI takibi sağlar.',
            color: '#ffea00'
        },
        {
            icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
            title: 'Sürekli Gelişim & Aksiyon',
            description: 'Makine öğrenimi ile tahmin doğruluğunu sürekli geliştirir ve veri odaklı aksiyon önerileri üretir.',
            color: '#ff1744'
        }
    ];

    const stats = [
        { label: 'Tahmin Doğruluğu', value: '%98.5', icon: <CheckCircleIcon />, color: '#00e676' },
        { label: 'Stok Maliyeti Düşüş', value: '%32', icon: <TrendingDownIcon />, color: '#2979ff' },
        { label: 'Satış Kaybı Önleme', value: '%85', icon: <TrendingUpIcon />, color: '#ffea00' },
        { label: 'Operasyonel Hız', value: '10x', icon: <SpeedIcon />, color: '#ff1744' },
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
            {/* Premium Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #091E42 0%, #102a5c 100%)', // Deep Blue for Corporate/Data feel
                    color: 'white',
                    pt: { xs: 12, md: 18 },
                    pb: { xs: 12, md: 18 },
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {/* Abstract Data Flow Background */}
                <Box
                    component={motion.div}
                    animate={{ x: [-50, 0, -50], opacity: [0.1, 0.3, 0.1] }}
                    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(41, 121, 255, 0.05) 10px, rgba(41, 121, 255, 0.05) 20px)',
                        pointerEvents: 'none'
                    }}
                />
                <Box
                    component={motion.div}
                    animate={{ scale: [1, 1.5, 1], opacity: [0.1, 0.2, 0.1] }}
                    transition={{ duration: 15, repeat: Infinity }}
                    sx={{
                        position: 'absolute',
                        top: '-20%',
                        right: '-10%',
                        width: '600px',
                        height: '600px',
                        background: 'radial-gradient(circle, rgba(0, 230, 118, 0.1) 0%, transparent 70%)',
                        borderRadius: '50%',
                    }}
                />

                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={8} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <Chip
                                    label="D-CAT Stok Zekası"
                                    sx={{
                                        bgcolor: 'rgba(41, 121, 255, 0.1)',
                                        color: '#448aff',
                                        border: '1px solid rgba(41, 121, 255, 0.3)',
                                        mb: 3,
                                        fontWeight: 600,
                                        px: 1
                                    }}
                                />
                                <Typography
                                    variant="h1"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: { xs: '2.5rem', md: '4rem' },
                                        mb: 2,
                                        background: 'linear-gradient(90deg, #fff 0%, #82b1ff 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        lineHeight: 1.1
                                    }}
                                >
                                    Geleceği Tahmin Edin, Stoğunuzu Yönetin
                                </Typography>
                                <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.7)', mb: 5, lineHeight: 1.6, fontWeight: 300, maxWidth: 650 }}>
                                    Satış geçmişini, sezonluk trendleri ve pazar verilerini analiz ederek gelecekteki talebi yüksek doğrulukla tahmin eden, optimum stok seviyelerini önererek operasyonel verimliliği maksimize eden yapay zeka destekli çözüm
                                </Typography>

                            </motion.div>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                <Paper
                                    elevation={24}
                                    sx={{
                                        background: 'rgba(15, 23, 42, 0.9)',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                        boxShadow: '0 20px 80px rgba(0,0,0,0.6)'
                                    }}
                                >
                                    {/* Dashboard Header */}
                                    <Box sx={{ p: 2.5, borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'rgba(0,0,0,0.2)' }}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Avatar sx={{ bgcolor: '#2979ff', width: 36, height: 36 }}>
                                                <InsightsIcon sx={{ fontSize: 20 }} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2 }}>
                                                    Akıllı Stok Paneli
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                                    AI Forecast Engine v2.4
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <Chip
                                            icon={<UpdateIcon sx={{ color: '#00e676 !important', fontSize: '1rem !important' }} />}
                                            label="Sistem Aktif"
                                            size="small"
                                            sx={{
                                                bgcolor: 'rgba(0, 230, 118, 0.1)',
                                                color: '#00e676',
                                                fontWeight: 600,
                                                border: '1px solid rgba(0, 230, 118, 0.2)'
                                            }}
                                        />
                                    </Box>

                                    {/* Dashboard Content */}
                                    <Box sx={{ p: 4 }}>
                                        {/* Main Chart Simulation */}
                                        <Box sx={{ mb: 4 }}>
                                            <Stack direction="row" justifyContent="space-between" mb={1}>
                                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>Gelecek Ay Tahmini (Adet)</Typography>
                                                <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>12,450 <span style={{ fontSize: '0.9rem', color: '#00e676' }}>(+%18)</span></Typography>
                                            </Stack>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-end', height: 60, gap: 1 }}>
                                                {[30, 45, 35, 50, 65, 55, 70, 85, 80, 95].map((h, i) => (
                                                    <motion.div
                                                        key={i}
                                                        initial={{ height: 0 }}
                                                        animate={{ height: `${h}%` }}
                                                        transition={{ duration: 1, delay: i * 0.1 }}
                                                        style={{
                                                            flex: 1,
                                                            background: i > 6 ? 'linear-gradient(to top, #2979ff, #82b1ff)' : 'rgba(255,255,255,0.1)',
                                                            borderRadius: 4
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>

                                        {/* Live Operations Feed */}
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 2, display: 'block', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
                                            ANLIK SİSTEM AKSİYONLARI
                                        </Typography>

                                        <Box sx={{ maxHeight: 220, overflow: 'hidden' }}>
                                            <AnimatePresence>
                                                {stockUpdates.map((item) => (
                                                    <motion.div
                                                        key={item.id}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        exit={{ opacity: 0, x: 20 }}
                                                        transition={{ duration: 0.4 }}
                                                    >
                                                        <Paper
                                                            sx={{
                                                                p: 2,
                                                                mb: 1.5,
                                                                bgcolor: 'rgba(255,255,255,0.03)',
                                                                borderRadius: 2,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                border: '1px solid rgba(255,255,255,0.05)'
                                                            }}
                                                        >
                                                            <Stack direction="row" spacing={2} alignItems="center">
                                                                <Avatar sx={{
                                                                    width: 32, height: 32,
                                                                    bgcolor: item.status === 'critical' ? 'rgba(255, 23, 68, 0.2)' : item.status === 'optimal' ? 'rgba(0, 230, 118, 0.2)' : 'rgba(255, 234, 0, 0.2)',
                                                                    color: item.status === 'critical' ? '#ff1744' : item.status === 'optimal' ? '#00e676' : '#ffea00'
                                                                }}>
                                                                    {item.status === 'critical' ? <WarningIcon fontSize="small" /> : item.status === 'optimal' ? <TrendingUpIcon fontSize="small" /> : <InventoryIcon fontSize="small" />}
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>{item.product}</Typography>
                                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{item.message}</Typography>
                                                                </Box>
                                                            </Stack>
                                                            <Chip
                                                                label={item.action}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{
                                                                    borderColor: 'rgba(255,255,255,0.2)',
                                                                    color: 'white',
                                                                    height: 24,
                                                                    fontSize: '0.7rem'
                                                                }}
                                                            />
                                                        </Paper>
                                                    </motion.div>
                                                ))}
                                            </AnimatePresence>
                                        </Box>
                                    </Box>
                                </Paper>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Stats Section with Floating Cards */}
            <Container maxWidth="xl" sx={{ mt: -6, position: 'relative', zIndex: 2, mb: 12 }}>
                <Grid container spacing={3}>
                    {stats.map((stat, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1, duration: 0.5 }}
                                viewport={{ once: true }}
                            >
                                <Paper
                                    elevation={10}
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        bgcolor: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        transition: 'all 0.3s',
                                        '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }
                                    }}
                                >
                                    <Avatar sx={{ bgcolor: `${stat.color}15`, color: stat.color, width: 56, height: 56 }}>
                                        {stat.icon}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase' }}>
                                            {stat.label}
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#091E42' }}>
                                            {stat.value}
                                        </Typography>
                                    </Box>
                                </Paper>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Features Grid */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Box textAlign="center" mb={10}>
                    <Typography variant="overline" sx={{ color: '#2979ff', fontWeight: 700, letterSpacing: 1.5 }}>
                        TEKNOLOJİ YETENEKLERİ
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#091E42', mt: 1, mb: 3 }}>
                        Veri Odaklı Stok Yönetimi
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, maxWidth: 700, mx: 'auto' }}>
                        Geleneksel stok yönetiminin ötesine geçin. Yapay zeka, işletmenizin görünmeyen verilerini analiz ederek karlılığınızı artırır.
                    </Typography>
                </Box>

                <Grid container spacing={4}>
                    {features.map((feature, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1, duration: 0.5 }}
                                viewport={{ once: true }}
                            >
                                <Card
                                    sx={{
                                        p: 4,
                                        height: '100%',
                                        borderRadius: 4,
                                        border: '1px solid rgba(0,0,0,0.05)',
                                        boxShadow: 'none',
                                        bgcolor: 'white',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: feature.color,
                                            boxShadow: `0 10px 40px ${feature.color}15`,
                                            transform: 'translateY(-5px)'
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: feature.color,
                                                color: 'white',
                                                width: 60,
                                                height: 60,
                                                boxShadow: `0 8px 20px ${feature.color}40`
                                            }}
                                        >
                                            {feature.icon}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#091E42' }}>
                                                {feature.title}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                                {feature.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Stratejik Vizyon */}
            <Box
                sx={{
                    py: { xs: 10, md: 14 },
                    background: 'linear-gradient(180deg, #091E42 0%, #0b2557 100%)',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0, left: 0, width: '100%', height: '100%',
                        background: 'radial-gradient(circle at 20% 30%, rgba(41, 121, 255, 0.18) 0%, transparent 55%), radial-gradient(circle at 85% 75%, rgba(124, 77, 255, 0.15) 0%, transparent 55%)',
                        pointerEvents: 'none'
                    }}
                />
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ textAlign: 'center', mb: 7 }}>
                        <Chip
                            label="STRATEJİK VİZYON"
                            sx={{
                                mb: 3,
                                px: 2,
                                py: 2.5,
                                fontWeight: 700,
                                letterSpacing: '0.15em',
                                fontSize: '0.78rem',
                                bgcolor: 'rgba(41, 121, 255, 0.2)',
                                color: '#7eb4ff',
                                border: '1px solid rgba(41, 121, 255, 0.35)'
                            }}
                        />
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                color: '#ffffff',
                                mb: 3,
                                fontSize: { xs: '2rem', md: '2.75rem' }
                            }}
                        >
                            Veriyle Şekillenen Gelecek
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.75)',
                                maxWidth: 820,
                                mx: 'auto',
                                fontSize: '1.05rem',
                                lineHeight: 1.85
                            }}
                        >
                            D-CAT Analytics olarak amacımız sadece tahmin yapmak değil, bu tahminleri iş stratejinizin ayrılmaz bir parçası haline getirmektir. Veriye dayalı öngörülerimizle operasyonel risklerinizi minimize ederken, büyüme potansiyelinizi maksimize ediyoruz.
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {[
                            {
                                icon: <InsightsIcon />,
                                title: 'Uçtan Uca Entegrasyon',
                                description: 'Mevcut ERP ve satış sistemlerinizle tam uyumlu veri akışı.',
                                color: '#2979ff'
                            },
                            {
                                icon: <UpdateIcon />,
                                title: 'Sürekli Model Eğitimi',
                                description: 'Değişen pazar koşullarına göre kendini güncelleyen algoritmalar.',
                                color: '#7c4dff'
                            },
                            {
                                icon: <AutoAwesomeIcon />,
                                title: 'Operasyonel Mükemmellik',
                                description: 'Tahminden aksiyona dönüşen, verimliliği odaklayan süreçler.',
                                color: '#00bfa5'
                            }
                        ].map((item, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                >
                                    <Box sx={{ textAlign: 'center', px: 2 }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: `${item.color}25`,
                                                color: item.color,
                                                width: 72,
                                                height: 72,
                                                mx: 'auto',
                                                mb: 3,
                                                border: `1px solid ${item.color}55`
                                            }}
                                        >
                                            {item.icon}
                                        </Avatar>
                                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#ffffff' }}>
                                            {item.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.7 }}>
                                            {item.description}
                                        </Typography>
                                    </Box>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Ücretsiz Demo Talep Et CTA */}
            <Box
                component={motion.div}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                sx={{
                    py: { xs: 8, md: 12 },
                    background: 'linear-gradient(180deg, #0b1426 0%, #0a1020 100%)',
                    textAlign: 'center'
                }}
            >
                <Container maxWidth="md">
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            color: '#ffffff',
                            mb: 3,
                            fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                            textShadow: '2px 0 0 rgba(255,60,80,0.55), -2px 0 0 rgba(60,170,255,0.55)',
                            letterSpacing: '-0.5px'
                        }}
                    >
                        Ücretsiz Demo Talep Et
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.72)',
                            maxWidth: 720,
                            mx: 'auto',
                            mb: 5,
                            fontSize: { xs: '1rem', md: '1.125rem' },
                            lineHeight: 1.7
                        }}
                    >
                        Yapay zeka çözümlerimizin işletmenize katacağı değeri birlikte keşfedelim. Hemen bizimle iletişime geçin ve uzman ekibimizden demo talep edin.
                    </Typography>
                    <Button
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => window.open('https://dcat.com.tr/classic/tr/iletisim', '_blank')}
                        sx={{
                            px: 5,
                            py: 1.75,
                            fontSize: '1.05rem',
                            fontWeight: 700,
                            color: '#fff',
                            borderRadius: 999,
                            textTransform: 'none',
                            background: 'linear-gradient(90deg, #3b5cff 0%, #6a4cff 100%)',
                            boxShadow: '0 0 40px rgba(79, 92, 255, 0.55), 0 8px 24px rgba(0,0,0,0.35)',
                            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #4a6bff 0%, #7a5cff 100%)',
                                boxShadow: '0 0 55px rgba(106, 76, 255, 0.7), 0 10px 28px rgba(0,0,0,0.4)',
                                transform: 'translateY(-2px)'
                            }
                        }}
                    >
                        Hemen Başlayın
                    </Button>
                </Container>
            </Box>
        </Box>
    );
};

export default DemandForecasting;
